import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {IntersectionObserver|undefined} */
  #screenObserver = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#screenObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.resume();
          else this.suspend();
        }
      },
      { rootMargin: '100px' }
    );
    this.#screenObserver.observe(this);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#screenObserver?.disconnect();
    this.#screenObserver = undefined;
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) {
      this.#current = current;
      return;
    }

    const previous = this.#relativeIndex(this.#current);
    const direction = current < this.#current ? 'backward' : 'forward';
    this.#current = current;
    const next = this.#relativeIndex(current);

    if (previous === next) return;

    /* La relève, en deux temps : l'annonce en place est marquée « sortante » — elle
       s'en va — pendant que la suivante prend sa place. */
    this.dataset.slideDirection = direction;

    const leaving = slides[previous];
    if (leaving instanceof HTMLElement) {
      leaving.dataset.outgoing = '';
      clearTimeout(this.#exitEnd);
      this.#exitEnd = setTimeout(() => {
        for (const slide of slides) {
          if (slide instanceof HTMLElement) delete slide.dataset.outgoing;
        }
      }, this.#pollDuration);
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== next}`);
    });
  }

  /** @param {number} rawValue */
  #relativeIndex(rawValue) {
    const total = (this.refs.slides ?? []).length;
    if (!total) return 0;
    return ((rawValue % total) + total) % total;
  }

  /** @type {number|undefined} */
  #exitEnd = undefined;

  /**
   * Durée de la relève, lue sur la feuille de style : la valeur ne vit qu'à un
   * seul endroit, et le marquage tombe exactement quand le mouvement finit.
   */
  get #pollDuration() {
    const slide = (this.refs.slides ?? [])[0];
    if (!(slide instanceof HTMLElement)) return 420;
    const rawValue = getComputedStyle(slide).getPropertyValue('--announcement-duration').trim();
    if (rawValue.endsWith('ms')) return parseFloat(rawValue) || 420;
    if (rawValue.endsWith('s')) return (parseFloat(rawValue) || 0.42) * 1000;
    return 420;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
